import "dayjs/locale/id";
import {
  AppstoreOutlined,
  BellOutlined,
  CalendarOutlined,
  IdcardOutlined,
  InboxOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Flex, Row, Skeleton, Typography } from "antd";
import DashboardMetricCard from "components/DashboardMetricCard.component";
import BookingRefSearch from "components/bookings/BookingRefSearch.component";
import {
  getDashboardAnalytics,
  getDashboardSchedules,
} from "hooks/queries/business";
import {
  getNotificationScroll,
  getTodayActivitySummary,
  NotificationCategory,
} from "hooks/queries/notification";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { translate, translateWithVars } from "shared/languages";
import { currentLang } from "i18n";
import { translateMessage } from "shared/utils";
import PATHS from "shared/constants/paths";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { useAuth } from "context/AuthContextProvider";
import { AdminCapability } from "shared/utils/capabilities";
import { colors } from "shared/theme/color";
import { now, tzDate } from "shared/utils/dayjs";
import {
  useMarkNotificationsSeen,
  useUpdateNotificationById,
} from "hooks/mutations/notification";
import { Notification } from "shared/types";
import { ClassCategory } from "shared/types/enum";
import { CalendarSchedule } from "components/WeeklyCalendar/components/types";
import { ScheduleStatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { NotificationDrawer } from "pages/notifications/NotificationDrawer";
import { NotificationFilterPills } from "pages/notifications/NotificationFilterPills";
import { ClashNotificationContent } from "pages/notifications/ClashNotificationContent";
import {
  CustomerBookingContent,
  customerBookingBody,
} from "pages/notifications/CustomerBookingContent";
import { NotificationIconBadge } from "pages/notifications/NotificationVisuals";
import { notificationContentHtml } from "pages/notifications/notificationText";
import { textPx } from "shared/constants/zoom";
import { sectionLabelStyle } from "components/NavSectionLabel.component";

/**
 * Time-of-day greeting on the DEVICE's own hour, the same bands as the client
 * and instructor apps: pagi before 12:00, siang until 15:00, sore until
 * 19:00, malam after (English: morning, afternoon until 19:00, evening). The
 * date under it follows the same clock, so the two always agree.
 */
const greetingKey = (): string => {
  const h = new Date().getHours();
  if (h < 12) return "dashboard.greeting.morning";
  if (h < 15) return "dashboard.greeting.afternoon";
  if (h < 19) return "dashboard.greeting.evening";
  return "dashboard.greeting.night";
};

const getScheduleStatus = (
  schedule: CalendarSchedule
): "in_progress" | "upcoming" | "past" => {
  const start = tzDate(schedule.start_time);
  const end = tzDate(schedule.end_time);
  const current = now();
  if (current.isAfter(start) && current.isBefore(end)) return "in_progress";
  if (current.isBefore(start)) return "upcoming";
  return "past";
};

// Status-colored left accent bar for the schedule list items. Matches
// ScheduleStatusBadge's colors exactly (navy for Ongoing, amber for
// Upcoming) rather than its own reversed mapping — ink-3 for past, which the
// list filters out.
const getScheduleBlockColor = (schedule: CalendarSchedule) => {
  const status = getScheduleStatus(schedule);
  if (status === "in_progress") return colors.navy;
  if (status === "upcoming") return colors.amber;
  return colors.ink3;
};

// Section heading: the shared section-label style.
const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      ...sectionLabelStyle,
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
    }}
  >
    {children}
  </span>
);

// Muted "this range vs last range" caption for a section eyebrow row. The
// ranges themselves say the comparison is same-days-so-far, so no explainer
// icon.
// The server labels a range as "1 Sep–15 Sep 2026". When both ends share the
// month, say it once ("1–15 Sep 2026"); anything else is left as sent.
const compactRange = (label: string): string =>
  label.replace(/^(\d+) ([A-Za-z]+)–(\d+) \2 (\d{4})$/, "$1–$3 $2 $4");

// The two ranges normally share a year; say it once, at the end.
const comparisonLabel = (comparison: {
  current_range: string;
  previous_range: string;
}): string => {
  const cur = compactRange(comparison.current_range);
  const prev = compactRange(comparison.previous_range);
  const year = (r: string) => (r.match(/(\d{4})$/) || [])[1];
  const y = year(cur);
  return y && y === year(prev)
    ? `${cur.replace(/ \d{4}$/, "")} vs ${prev}`
    : `${cur} vs ${prev}`;
};

const ComparisonCaption = ({
  comparison,
}: {
  comparison: { current_range: string; previous_range: string };
}) => (
  <span
    className="tnum"
    style={{
      display: "inline-flex",
      alignItems: "center",
      color: colors.ink2,
      fontSize: textPx(12.5),
      fontWeight: 500,
      whiteSpace: "nowrap",
    }}
  >
    {comparisonLabel(comparison)}
  </span>
);

// Navy-tinted quick-action button (the active mini-stat pairing: navyTint
// surface + navyDark text) — reads as a quick tool, not a shouting primary
// CTA. Custom element because AntD's Button hover tokens would fight the
// tinted surface.
// Every section label row on the dashboard is this tall (the bell button in
// the Notifikasi row is the tallest thing in any of them), so the cards
// under the three rows all start on the same line.
const SECTION_LABEL_ROW_HEIGHT = 40;

const QUICK_ACTION_BORDER = "rgba(46, 63, 139, 0.20)";
const QUICK_ACTION_BORDER_HOVER = "rgba(46, 63, 139, 0.38)";
const QUICK_ACTION_BG_HOVER = "#D8DEF0"; // one step deeper than navyTint

// On the navy header band the chips are translucent white ("glass") so they
// belong to the band instead of floating on it; the light-surface variant is
// kept for any future use on the page ground.
const QUICK_ACTION_DARK_BG = "rgba(250, 250, 247, 0.12)";
const QUICK_ACTION_DARK_BG_HOVER = "rgba(250, 250, 247, 0.22)";
const QUICK_ACTION_DARK_BORDER = "rgba(250, 250, 247, 0.22)";
const QUICK_ACTION_DARK_BORDER_HOVER = "rgba(250, 250, 247, 0.4)";

const QuickActionButton = ({
  icon,
  label,
  onClick,
  onDark = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  onDark?: boolean;
}) => {
  const [hover, setHover] = useState(false);
  const background = onDark
    ? hover
      ? QUICK_ACTION_DARK_BG_HOVER
      : QUICK_ACTION_DARK_BG
    : hover
      ? QUICK_ACTION_BG_HOVER
      : colors.navyTint;
  const border = onDark
    ? hover
      ? QUICK_ACTION_DARK_BORDER_HOVER
      : QUICK_ACTION_DARK_BORDER
    : hover
      ? QUICK_ACTION_BORDER_HOVER
      : QUICK_ACTION_BORDER;
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        height: 36,
        padding: "0 16px",
        borderRadius: 8,
        background,
        border: `1px solid ${border}`,
        color: onDark ? colors.page : colors.navyDark,
        fontSize: textPx(13.5),
        fontWeight: 600,
        cursor: "pointer",
        transition: "background 130ms ease, border-color 130ms ease",
      }}
    >
      <span style={{ fontSize: textPx(14), display: "inline-flex" }}>{icon}</span>
      {label}
    </button>
  );
};

// Empty state for the Hari Ini cards: an icon chip, the message, and one
// action link. A lone grey sentence made an empty card look broken; this
// makes an empty day look like a calm day.
const EmptyState = ({
  icon,
  message,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  message: string;
  actionLabel: string;
  onAction: () => void;
}) => (
  <Flex align="center" gap={14} style={{ padding: "6px 0 2px" }}>
    <div
      style={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: 12,
        background: colors.bgSoft,
        color: colors.ink3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: textPx(18),
      }}
    >
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <Typography.Text style={{ display: "block", color: colors.ink2 }}>
        {message}
      </Typography.Text>
      <Button
        type="link"
        onClick={onAction}
        style={{ padding: 0, height: "auto", fontWeight: 600, color: colors.navy }}
      >
        {actionLabel}
      </Button>
    </div>
  </Flex>
);

// The two dashboard lists' rows are clickable cards, not links or buttons, so
// they carry role="link", a tab stop and this handler: without them the rows
// could only be opened with a mouse (Tab skipped them, Enter did nothing).
// The focus ring is .dashboard-row:focus-visible in index.css.
const activateOnKey = (action: () => void) => (e: React.KeyboardEvent) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  // Space would otherwise also scroll the page.
  e.preventDefault();
  action();
};

// Dashboard list row (schedules): hairline card surface with a status-colored
// left accent bar; hover swaps to the bg-soft surface.
const ListItem = ({
  accentColor,
  onClick,
  children,
}: {
  accentColor?: string;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="dashboard-row"
      role="link"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={activateOnKey(onClick)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 14,
        width: "100%",
        padding: "13px 14px 13px 18px",
        borderRadius: 12,
        border: `1px solid ${hover ? colors.lineStrong : colors.line}`,
        background: hover ? colors.bgSoft : colors.card,
        cursor: "pointer",
        transition: "background 150ms ease, border-color 150ms ease",
      }}
    >
      {accentColor && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 12,
            bottom: 12,
            width: 3,
            borderRadius: 999,
            background: accentColor,
          }}
        />
      )}
      {children}
    </div>
  );
};

// Compact dashboard notification item (notification prototype .mini): the
// shared icon badge + unseen treatment, title + relative timestamp, and the
// body — a rich clash card when structured, a class/when/outcome layout for
// customer booking events, otherwise a 2-line excerpt.
const NotificationMiniItem = ({
  notification,
  onClick,
}: {
  notification: Notification;
  onClick: () => void;
}) => {
  const [hover, setHover] = useState(false);
  const unseen = !notification.seen;
  const booking = customerBookingBody(notification);
  // Same surface as the schedule rows beside it (white, hairline border,
  // bg-soft on hover) so the two Hari Ini lists read as one family. Unseen
  // is a navy accent bar + dot, the page's one accent colour; the drawer keeps
  // the shared amber treatment.
  return (
    <div
      className="dashboard-row"
      role="link"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={activateOnKey(onClick)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        display: "flex",
        gap: 12,
        padding: "12px 14px 12px 16px",
        borderRadius: 12,
        border: `1px solid ${hover ? colors.lineStrong : colors.line}`,
        background: hover ? colors.bgSoft : colors.card,
        cursor: "pointer",
        transition: "background 150ms ease, border-color 150ms ease",
      }}
    >
      {unseen && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 12,
            bottom: 12,
            width: 3,
            borderRadius: 999,
            background: colors.navy,
          }}
        />
      )}
      <NotificationIconBadge size={30} type={notification.type} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Flex justify="space-between" align="flex-start" gap={8}>
          <Typography.Text
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: textPx(13),
              lineHeight: 1.3,
              fontWeight: unseen ? 600 : 500,
              color: colors.ink,
            }}
          >
            {translateMessage(notification.title)}
          </Typography.Text>
          <span
            style={{
              fontSize: textPx(11),
              color: colors.ink3,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              flexShrink: 0,
            }}
          >
            {unseen && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: colors.navy,
                  flexShrink: 0,
                }}
              />
            )}
            {notification.relative_created_at}
          </span>
        </Flex>
        {notification.payload && "groups" in notification.payload ? (
          <ClashNotificationContent payload={notification.payload} />
        ) : booking ? (
          <CustomerBookingContent body={booking} />
        ) : (
          notification.content?.trim() && (
            <div
              dangerouslySetInnerHTML={{
                __html: notificationContentHtml(notification),
              }}
              style={{
                fontSize: textPx(12),
                color: colors.ink3,
                marginTop: 2,
                lineHeight: 1.45,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            />
          )
        )}
      </div>
    </div>
  );
};


// Client self-service summary that heads the Notifikasi panel: a plain-language
// scope line + three tappable, scope-labelled mini-stats (booking / waitlist /
// cancel via the app today). The stats share the feed's icons and drive the
// same filter the pills do (setNotifCategory), so a count is one tap from the
// rows that prove it. This is the ONLY place today's client counts appear —
// there is no second "Booking" big-counter tile anywhere on the page.
const ClientActivitySummary = ({
  summary,
  activeCategory,
  onSelect,
}: {
  summary?: { bookings: number; waitlist: number; cancellations: number };
  activeCategory: NotificationCategory;
  onSelect: (next: NotificationCategory) => void;
}) => {
  const bookings = summary?.bookings ?? 0;
  const waitlist = summary?.waitlist ?? 0;
  const cancellations = summary?.cancellations ?? 0;
  const empty = bookings + waitlist + cancellations === 0;
  // A studio with the waitlist switched off has no waitlist activity to
  // count, unless some happened earlier today before the switch.
  const { data: settings } = useBusinessSettings();
  const hideWaitlist = settings?.waitlist_enabled === false && waitlist === 0;

  const stats: {
    category: NotificationCategory;
    value: number;
    label: string;
  }[] = [
    {
      category: "booking",
      value: bookings,
      label: translate("dashboard.client_activity.booking"),
    },
    ...(hideWaitlist
      ? []
      : [
          {
            category: "waitlist" as NotificationCategory,
            value: waitlist,
            label: translate("dashboard.client_activity.waitlist"),
          },
        ]),
    {
      category: "cancel",
      value: cancellations,
      label: translate("dashboard.client_activity.cancel"),
    },
  ];

  // One short caption above the strip says what the numbers are: what
  // clients did THEMSELVES in the app today, not every booking the studio
  // took. Without it "7 Booking" next to today's schedule reads as the day's
  // total. Kept to one quiet line, not a heading. The empty state keeps its
  // own sentence, since there is no strip then.
  return (
    <div style={{ marginBottom: 14 }}>
      {!empty && (
        <Typography.Text
          style={{
            fontSize: textPx(12),
            color: colors.ink3,
            display: "block",
            lineHeight: 1.4,
            marginBottom: 8,
          }}
        >
          {translate("dashboard.client_activity.lead")}
        </Typography.Text>
      )}
      {empty && (
        <Typography.Text
          style={{
            fontSize: textPx(12.5),
            color: colors.ink2,
            display: "block",
            lineHeight: 1.45,
          }}
        >
          {translate("dashboard.client_activity.empty")}
        </Typography.Text>
      )}
      {/* Same surface as the KPI cards above it (white, hairline border,
          hairline dividers, number over label) so the rail opens like the
          rest of the page rather than with a grey control box. Each segment
          is still a tap filter; the selected one carries the navy tint. */}
      {!empty && (
        <Flex
          className="dashboard-activity-strip"
          style={{
            background: colors.card,
            border: `1px solid ${colors.line}`,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {stats.map((stat, i) => {
            const active = activeCategory === stat.category;
            return (
              <button
                key={stat.category}
                type="button"
                className={`dashboard-activity-segment${active ? " dashboard-activity-segment-active" : ""}`}
                onClick={() => onSelect(active ? "all" : stat.category)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 2,
                  padding: "12px 14px",
                  border: "none",
                  borderLeft: i === 0 ? "none" : `1px solid ${colors.line}`,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <Typography.Text
                  className="display tnum"
                  strong
                  style={{
                    fontSize: textPx(20),
                    lineHeight: 1.1,
                    color: active ? colors.navyDark : colors.ink,
                  }}
                >
                  {stat.value}
                </Typography.Text>
                <Typography.Text
                  style={{
                    fontSize: textPx(11.5),
                    lineHeight: 1.2,
                    color: active ? colors.navy : colors.ink3,
                  }}
                >
                  {stat.label}
                </Typography.Text>
              </button>
            );
          })}
        </Flex>
      )}
    </div>
  );
};

export const DashboardPage = () => {
  const navigate = useNavigate();
  // Pembatasan Admin: the three catalogue shortcuts are navigation, so they
  // hide (their targets are read-only or redirect); Jadwal Baru stays.
  const { isRestricted } = useAdminCapabilities();
  const catalogRestricted = isRestricted(AdminCapability.CatalogPricing);
  const [notifCategory, setNotifCategory] =
    useState<NotificationCategory>("all");
  // The bell and the empty-state "Lihat Semua" link open the same drawer.
  const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);

  const { data: analytics, isLoading: analyticsLoading } =
    getDashboardAnalytics();
  const { data: schedulesData, isLoading: schedulesLoading } =
    getDashboardSchedules();
  // Today-only feed (server-filtered), so the rail's first page is today's
  // rows even when older unread rows would otherwise sort first.
  const { data: notificationData } = getNotificationScroll(true, notifCategory, {
    today: true,
  });
  const { data: todayActivity } = getTodayActivitySummary();
  const { mutate: updateNotification } = useUpdateNotificationById();
  const { mutate: markNotificationsSeen } = useMarkNotificationsSeen();

  // v2 search returns every occurrence in today's range, unsorted and
  // unbounded — the BE no longer trims to upcoming or limits to 5, so do it
  // here.
  // Every class still to come today, in start order. The heading counts this
  // full list (the real number left today, like the rail's "Lihat Semua (N)");
  // the card lists only the first five.
  const upcomingToday = useMemo(() => {
    if (!schedulesData) return [];
    const currentTime = now();
    return schedulesData
      .filter((s) => !tzDate(s.end_time).isBefore(currentTime))
      .sort(
        (a, b) =>
          tzDate(a.start_time).valueOf() - tzDate(b.start_time).valueOf()
      );
  }, [schedulesData]);
  const todaySchedules = useMemo(
    () => upcomingToday.slice(0, 5),
    [upcomingToday]
  );

  // The dashboard rail shows only TODAY's notifications, capped at 5, and
  // sizes to its content rather than stretching to the schedule column (a
  // stretched card with white space under the last row read as missing
  // content). The card always ends on a "Lihat Semua (N)" link into the side
  // drawer (the bell), which holds the overflow and the history.
  //
  // Three counters, one job each: the bell badge is the studio-wide UNSEEN
  // count (server); "Lihat Semua (N)" is TODAY's total (server, all
  // categories); the rail is today's first five in feed order. The feed is
  // ordered UNSEEN FIRST, then newest (server), so the rail surfaces what
  // nobody has looked at before what they have. Showing a row here counts
  // as seeing it (the effect below), so the badge only keeps counting what
  // has not been on screen: today's overflow behind "Lihat Semua" (which the
  // rail brings forward on the next visit) and older days (drawer only).
  const todayNotifications = useMemo(
    () => notificationData?.pages.flatMap((p) => p.data) ?? [],
    [notificationData],
  );
  const notifications = useMemo(
    () => todayNotifications.slice(0, 5),
    [todayNotifications],
  );
  const todayCount = todayActivity?.total ?? todayNotifications.length;

  // Rows shown in the rail are "seen". Runs after the commit (so the rows have
  // painted), only while the tab is visible (a background refetch must not
  // clear the badge for rows nobody looked at), and once per id for the life
  // of the page (a re-render or refetch never re-sends). The rows keep their
  // "new" accent for this visit because this refreshes only the badge.
  const seenSentRef = useRef<Set<string>>(new Set());
  // A page opened in a background tab renders with the tab hidden, and the
  // focus refetch may return identical data (no re-render), so also re-run
  // the effect when the tab becomes visible.
  const [visibilityTick, setVisibilityTick] = useState(0);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") setVisibilityTick((n) => n + 1);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);
  useEffect(() => {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const ids = notifications
      .filter((n) => !n.seen && !seenSentRef.current.has(n.id))
      .map((n) => n.id);
    if (ids.length === 0) return;
    ids.forEach((id) => seenSentRef.current.add(id));
    markNotificationsSeen(ids);
  }, [notifications, markNotificationsSeen, visibilityTick]);

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      // Skip the write when the rail already marked this row seen on render.
      if (!notification.seen && !seenSentRef.current.has(notification.id)) {
        updateNotification(notification.id);
      }
      // Plain push (the drawer does the same) so Back returns to the dashboard.
      if (notification.link) navigate(notification.link);
    },
    [navigate, updateNotification]
  );

  // Monthly activity metrics (this month vs last), shown at the top.
  const activityCards = analytics
    ? [
        {
          title: translate("dashboard.metric.sessions_held"),
          icon: <UnorderedListOutlined />,
          tone: "navy" as const,
          info: translate("dashboard.metric.sessions_held_info"),
          ...analytics.sessions_held,
        },
        {
          title: translate("dashboard.metric.bookings"),
          icon: <CalendarOutlined />,
          tone: "navy" as const,
          info: translate("dashboard.metric.bookings_info"),
          ...analytics.bookings,
        },
        {
          title: translate("dashboard.metric.active_customers"),
          icon: <TeamOutlined />,
          tone: "navy" as const,
          info: translate("dashboard.metric.active_customers_info"),
          ...analytics.active_customers,
        },
      ]
    : [];

  // "Welcome back" in both languages (English is the universal form and a
  // time-of-day greeting ran on studio time, so viewers elsewhere got the
  // wrong one). Addressed by first name; a full "Welcome back, Christian
  // Andrew S." reads like a form letter. The studio and date sit beside it.
  const { state: authState } = useAuth();
  const firstName = authState.user?.full_name?.trim().split(/\s+/)[0];
  const studioName = authState.business?.name;

  const sectionCardStyle: React.CSSProperties = {
    flex: 1,
    borderRadius: 16,
    border: `1px solid ${colors.line}`,
    boxShadow: "none",
    width: "100%",
  };

  return (
    <div style={{ paddingTop: 32, paddingBottom: 40 }}>
      {/* Header band. Navy is the brand colour and this is the page's one
          brand moment: the greeting (who, where, when) on the left, the
          tools (booking-code search + quick actions) on the right, inside
          the same block. Content-dense on purpose; the earlier ink hero was
          a dark slab with three words in a corner. Below md the tools wrap
          under the greeting. */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          marginBottom: 32,
          borderRadius: 16,
          background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.navyDark} 100%)`,
          color: colors.page,
          padding: "28px 32px",
          // Same height as the three-line version, so dropping the subtitle
          // didn't shrink the band; the inner row stretches to fill it.
          minHeight: 140,
          display: "flex",
        }}
      >
        {/* Three lines on the left (where / who / when); the chips sit on
            the band's bottom edge on the right, level with the date line. */}
        <Flex
          justify="space-between"
          align="center"
          gap={24}
          wrap="wrap"
          style={{ position: "relative", width: "100%" }}
        >
          <div style={{ flex: "1 1 320px", minWidth: 240 }}>
            {studioName && (
              <Typography.Text
                style={{
                  display: "block",
                  fontSize: textPx(11),
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  lineHeight: `${textPx(16)}px`,
                  color: "rgba(250, 250, 247, 0.72)",
                }}
              >
                {studioName}
              </Typography.Text>
            )}
            <Typography.Title
              level={2}
              className="display"
              style={{
                margin: "8px 0 6px",
                fontSize: textPx(28),
                lineHeight: 1.1,
                color: colors.page,
              }}
            >
              {translate(greetingKey())}
              {firstName ? `, ${firstName}.` : "."}
            </Typography.Title>
            <Typography.Text
              style={{
                display: "block",
                fontSize: textPx(14),
                lineHeight: `${textPx(20)}px`,
                color: "rgba(250, 250, 247, 0.78)",
              }}
            >
              {dayjs().locale(currentLang()).format("dddd, DD MMMM YYYY")}
            </Typography.Text>
          </div>

          {/* Pembatasan Admin hides the three catalogue shortcuts (see
              catalogRestricted above). Wraps under the greeting below md. */}
          <Flex
            gap={12}
            wrap="wrap"
            className="dashboard-header-actions"
            style={{ alignSelf: "flex-end" }}
          >
            {!catalogRestricted && (
              <>
                <QuickActionButton
                  onDark
                  icon={<InboxOutlined />}
                  label={translate("dashboard.quick_actions.add_package")}
                  onClick={() => navigate(`${PATHS.PACKAGE_NEW}?type=credit`)}
                />
                <QuickActionButton
                  onDark
                  icon={<IdcardOutlined />}
                  label={translate("dashboard.quick_actions.add_membership")}
                  onClick={() => navigate(PATHS.MEMBERSHIP_NEW)}
                />
                <QuickActionButton
                  onDark
                  icon={<AppstoreOutlined />}
                  label={translate("dashboard.quick_actions.new_class")}
                  onClick={() =>
                    navigate(PATHS.CLASS, { state: { openCreate: true } })
                  }
                />
              </>
            )}
            <QuickActionButton
              onDark
              icon={<CalendarOutlined />}
              label={translate("dashboard.quick_actions.new_schedule")}
              onClick={() =>
                navigate(PATHS.SCHEDULE, { state: { openCreate: true } })
              }
            />
          </Flex>
        </Flex>
      </div>

      {/* Below the band the page is a main column (this month's numbers,
          then today's schedule) with Notifications as a right rail running
          the full height beside it. Three stacked full-width strips read as
          heavy even when each is calm; a two-column composition fills the
          page's height and gives the feed the vertical room a feed wants.
          Below xl the rail drops under the main column. */}
      <Row gutter={[24, 24]} align="stretch">
        <Col
          xs={24}
          xl={16}
          style={{ display: "flex", flexDirection: "column", gap: 24 }}
        >
          {/* This month's studio-wide performance — the ONLY big-counter
              surface */}
          <div>
            <Flex
              justify="space-between"
              align="center"
              gap={12}
              wrap="wrap"
              style={{ minHeight: SECTION_LABEL_ROW_HEIGHT }}
            >
              <Eyebrow>{translate("dashboard.metric.section_monthly")}</Eyebrow>
              {analytics?.comparison && (
                <ComparisonCaption comparison={analytics.comparison} />
              )}
            </Flex>
            <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
              {analyticsLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <Col key={i} xs={24} md={8}>
                      <Card style={{ borderRadius: 16 }}>
                        <Skeleton active paragraph={{ rows: 2 }} />
                      </Card>
                    </Col>
                  ))
                : activityCards.map((card) => (
                    <Col key={card.title} xs={24} md={8}>
                      <DashboardMetricCard {...card} />
                    </Col>
                  ))}
            </Row>
          </div>

          {/* Today's schedule. The booking-code search sits on this section's
              label row (right-aligned, mirroring the comparison caption on
              the Performa row above): it deep-links to a booking's
              occurrence on the calendar, so it belongs with today's
              schedule, not the greeting. */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Flex
              justify="space-between"
              align="center"
              gap={12}
              wrap="wrap"
              style={{ minHeight: SECTION_LABEL_ROW_HEIGHT }}
            >
              <Eyebrow>{translate("dashboard.today")}</Eyebrow>
              <BookingRefSearch />
            </Flex>
            <Card
              style={{ ...sectionCardStyle, marginTop: 12 }}
              styles={{ body: { padding: 22 } }}
            >
              <Flex
                justify="space-between"
                align="flex-start"
                style={{ marginBottom: 16 }}
                wrap="wrap"
                gap={8}
              >
                <div>
                <Typography.Title
                  level={5}
                  className="display"
                  style={{ margin: "4px 0 0" }}
                >
                  {translate("dashboard.upcoming_schedules")}{" "}
                  <Typography.Text
                    style={{ color: colors.ink3, fontWeight: 600 }}
                  >
                    ({upcomingToday.length})
                  </Typography.Text>
                </Typography.Title>
              </div>
              <Button type="primary" onClick={() => navigate(PATHS.SCHEDULE)}>
                {translate("dashboard.view_calendar")}
              </Button>
            </Flex>

            {schedulesLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : todaySchedules.length === 0 ? (
              <EmptyState
                icon={<CalendarOutlined />}
                message={translate("dashboard.no_schedules_today")}
                actionLabel={translate("dashboard.empty.create_schedule")}
                onAction={() =>
                  navigate(PATHS.SCHEDULE, { state: { openCreate: true } })
                }
              />
            ) : (
              <Flex vertical gap={10}>
                {todaySchedules.map((schedule) => {
                  const start = tzDate(schedule.start_time).format("HH:mm");
                  const end = tzDate(schedule.end_time).format("HH:mm");
                  // Open classes answer "how full" (booked / capacity). A
                  // private class has an exact party size, so it answers
                  // "who": names while they fit (up to three), the party
                  // count alone beyond that. A name list on an open class
                  // only ever got silently ellipsised.
                  const isOpenClass =
                    schedule.class_category === ClassCategory.Open &&
                    schedule.capacity != null;
                  // Open: seats taken (the cores' seat rule, so a refunded
                  // seat still counts and "full" means full). Private: the
                  // attendee names, which is what the party is.
                  const names = schedule.customer_names;
                  const customerLabel = isOpenClass
                    ? translateWithVars("dashboard.open_class_seats", {
                        booked: schedule.num_of_bookings ?? names.length,
                        capacity: schedule.capacity,
                      })
                    : names.length === 0
                      ? translate("dashboard.no_bookings_short")
                      : names.length > 3
                        ? translateWithVars("dashboard.private_class_party", {
                            count: names.length,
                          })
                        : names.join(", ");
                  // Demand behind a full open class: shown only when someone
                  // is actually queued, so every other row is untouched.
                  const waitlistLabel =
                    isOpenClass && (schedule.waitlist_count ?? 0) > 0
                      ? translateWithVars("dashboard.open_class_waitlist", {
                          count: schedule.waitlist_count,
                        })
                      : null;
                  return (
                    <ListItem
                      key={schedule.schedule_id}
                      accentColor={getScheduleBlockColor(schedule)}
                      onClick={() =>
                        navigate(`${PATHS.SCHEDULE}/${schedule.schedule_id}`)
                      }
                    >
                      <div style={{ minWidth: 0, flex: "1 1 200px" }}>
                        <Typography.Text
                          strong
                          style={{ fontSize: textPx(14), display: "block" }}
                        >
                          {schedule.class_name}
                        </Typography.Text>
                        <Typography.Text
                          style={{
                            fontSize: textPx(13),
                            display: "block",
                            marginTop: 6,
                            color: colors.ink3,
                          }}
                          ellipsis
                        >
                          {start}–{end} • {schedule.instructor_name} •{" "}
                          {customerLabel}
                          {waitlistLabel && ` • ${waitlistLabel}`}
                        </Typography.Text>
                      </div>
                      <ScheduleStatusBadge schedule={schedule} />
                    </ListItem>
                  );
                })}
              </Flex>
            )}
            </Card>
          </div>
        </Col>

        {/* Notifications rail — today only; older ones live in the drawer
            (the bell). Its title is the section eyebrow, like the two
            sections beside it, so the card itself opens straight into the
            activity summary. */}
        <Col
          xs={24}
          xl={8}
          style={{ display: "flex", flexDirection: "column", alignSelf: "flex-start" }}
        >
          <Flex
            justify="space-between"
            align="center"
            gap={12}
            wrap="wrap"
            style={{ minHeight: SECTION_LABEL_ROW_HEIGHT }}
          >
            <Eyebrow>{translate("dashboard.notifications")}</Eyebrow>
            <NotificationDrawer
              open={notifDrawerOpen}
              onOpenChange={setNotifDrawerOpen}
            />
          </Flex>
          <Card
            style={{ ...sectionCardStyle, flex: "none", marginTop: 12 }}
            styles={{ body: { padding: 22 } }}
          >

            {/* Client self-service activity — the emphasized story, scoped to
                the app + today, sitting directly above the feed that proves it */}
            <ClientActivitySummary
              summary={todayActivity}
              activeCategory={notifCategory}
              onSelect={setNotifCategory}
            />

            <div style={{ marginBottom: 14 }}>
              <NotificationFilterPills
                value={notifCategory}
                onChange={setNotifCategory}
                exclude={["booking", "waitlist", "cancel"]}
              />
            </div>

            {notifications.length === 0 ? (
              <EmptyState
                icon={<BellOutlined />}
                message={translate("dashboard.no_notifications")}
                actionLabel={translate("dashboard.view_all")}
                onAction={() => setNotifDrawerOpen(true)}
              />
            ) : (
              <>
                <Flex vertical gap={10}>
                  {notifications.map((notification) => (
                    <NotificationMiniItem
                      key={notification.id}
                      notification={notification}
                      onClick={() => handleNotificationClick(notification)}
                    />
                  ))}
                </Flex>
                {/* The card ends on the way to the rest: the drawer holds
                    today's overflow and the full history. The count is
                    TODAY's total (the heading stays a plain label like the
                    other two sections); the bell badge says how many are
                    unseen. */}
                <Button
                  type="link"
                  onClick={() => setNotifDrawerOpen(true)}
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 12,
                    paddingTop: 12,
                    height: "auto",
                    borderTop: `1px solid ${colors.line}`,
                    borderRadius: 0,
                    fontWeight: 600,
                    color: colors.navy,
                  }}
                >
                  {`${translate("dashboard.view_all")} (${todayCount})`}
                </Button>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;
